import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { Breadcrumb, IBreadcrumbItem } from '@fluentui/react/lib/Breadcrumb';
import type { FilePickerBreadcrumb } from '../store/schema/FilePickerViewState';
import { isFeatureEnabled } from 'owa-feature-flags';
import { SearchBox as FabricSearchBox } from '@fluentui/react/lib/SearchBox';
import { SearchBox } from 'owa-search';
import { SearchScenarioId } from 'owa-search-store';
import { Spinner } from '@fluentui/react/lib/Spinner';

const styles = require('./FilePickerPaneTopPart.scss');

export interface SearchBoxProps {
    isSearchEnabled: boolean;
    onSearch: (searchQuery: string) => void;
    onExitSearch: () => void;
    labelText: string;
    currentSearchText: string;
    isUsing3S: boolean;
}

export interface FilePickerPaneTopPartProps {
    breadcrumbs: FilePickerBreadcrumb[];
    onBreadcrumbSelected: (breadcrumb: FilePickerBreadcrumb) => void;
    spinnerEnabled: boolean;
    searchBoxProps: SearchBoxProps;
}

export default observer(function FilePickerPaneTopPart(props: FilePickerPaneTopPartProps) {
    const breadcrumbs: IBreadcrumbItem[] = props.breadcrumbs
        ? getBreadcrumbs(props.breadcrumbs, props.onBreadcrumbSelected)
        : [];
    const searchBox = isFeatureEnabled('doc-filePicker-mailMessageSearch') ? (
        <div className={styles.searchBoxBorder}>
            <SearchBox
                isUsing3S={props.searchBoxProps.isUsing3S}
                scenarioId={SearchScenarioId.FilesPicker}
                searchPlaceHolderText={props.searchBoxProps.labelText}
            />
        </div>
    ) : (
        <FabricSearchBox
            value={props.searchBoxProps.currentSearchText}
            onSearch={props.searchBoxProps.onSearch}
            onClear={props.searchBoxProps.onExitSearch}
            labelText={props.searchBoxProps.labelText}
        />
    );
    // Used to modify the padding on the loading spinner based on if the search box is present or not.
    // Also used to create an empty div in place of the spinner when not loading items.
    const spinnerClassName = props.searchBoxProps.isSearchEnabled
        ? styles.loadingSpinnerWithSearch
        : styles.loadingSpinnerNoSearch;
    return (
        <div className={styles.container}>
            <div className={styles.breadcrumbContainer}>
                {breadcrumbs.length !== 0 && (
                    <Breadcrumb
                        className={styles.breadcrumb}
                        items={breadcrumbs}
                        styles={{ chevron: styles.breadcrumbChevron }}
                    />
                )}
            </div>

            {props.spinnerEnabled ? (
                <Spinner className={spinnerClassName} />
            ) : (
                <div className={spinnerClassName} />
            )}

            {props.searchBoxProps.isSearchEnabled && (
                <div className={styles.searchBox}>{searchBox}</div>
            )}
        </div>
    );
});

function getBreadcrumbs(
    breadcrumbs: FilePickerBreadcrumb[],
    onBreadcrumbSelected: (breadcrumb: FilePickerBreadcrumb) => void
): IBreadcrumbItem[] {
    return breadcrumbs.map((b: FilePickerBreadcrumb, index: number) => {
        // If the user clicks on the last breadcrumb we do not want to do anything. This is because the normal action (onBreadCrumbSelected)
        // would clear the items, making the user wait while we reload the same items from the data provider. It would also clear the
        // users item selection, and sort.
        const onBreadcrumbClicked =
            index === breadcrumbs.length - 1
                ? (event: any, item: IBreadcrumbItem) => null
                : (event: any, item: IBreadcrumbItem) => onBreadcrumbSelected(b);
        return {
            key: index.toString(),
            text: b.name,
            onClick: onBreadcrumbClicked,
        } as IBreadcrumbItem;
    });
}
